import { useEffect, useState } from 'react';
import axios from 'axios';
import useConsole from '../Console/useConsole';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import utils from '../../utils/utils';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const MT_BASE_URL = ApiEndpoints.MODULE_BASE_URL.MT;
const ME_BASE_URL = ApiEndpoints.MODULE_BASE_URL.ME;
const totalPageCount = 'x-total-count';

/**
 * @function usePerformanceTesting
 * @description hook for PerformanceTesting submodule of model testing module
 * @param {function} fetchModuleTasks - function for fetching module tasks
 * @param {array} datasetList - list of datasets
 * @param {function} fetchDatasetList - function for fetching dataset list
 * @param {function} fetchDatasetRowsWithPagination - function for fetching dataset rows with pagination
 * @param {array} datasetRowsWithPagination - list of dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - function for setting dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - boolean for showing loader
 * @param {function} updateDatasetCells - function for updating dataset cells
 * @param {object} project - project object
 * @returns {object} - returning multiple methods and
 *  states for PerformanceTesting submodule of model testing module
 * @example const {fetchModuleTasks,fetchDatasetList,datasetList}=usePerformanceTesting()
 */
export default function usePerformanceTesting() {
  const {
    fetchModuleTasks,
    datasetList,
    fetchDatasetList,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,
    project,
    updateDatasetCells,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();
  const { markDatasetAsReadyToUse } = useDataIngestion();
  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();
  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);
  // For Active Operation
  const [activeOperation, setActiveOperation] = useState(
    MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.PT.operations.OO
  );

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // Performance Testing Tasks
  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});

  // all models data
  const [allModelData, setAllModelData] = useState([]);
  // Target variables/columns for dropdown
  const [targetVariables, setTargetVariables] = useState([]);

  // Metrics and Rules State
  const [testingConfig, updateTestingConfig] = useState({});

  // reset data on Operation and Dataset Change
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  /**
   * @function fetchPTModuleTasks
   * @description API call for fetching PerformanceTesting module tasks
   * @param {number} project_id - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {undefined}
   * @example fetchPTModuleTasks(project_id,hideLoaderOnRequest);
   */
  const fetchPTModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.PT.key,
        'GET_SS_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };

  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.PT.key,
        'GET_SS_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchPTModuleTasks
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {undefined}
   * @example fetchPTModuleTasks(project_id,hideLoaderOnRequest);
   */
  const fetchPTModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.PT.key,
        'GET_SS_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {object} datasetName - object containing dataset_name and project_id
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnDetails(datasetName);
   */
  const fetchDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.MT +
          ApiEndpoints.MT.DATASET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.columns);
        },
        () => {}
      );

  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   *  using task id, result will contain date like graph and charts
   * @param {string} taskID - task id
   * @param {object} additionTaskDetails - addition task details
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example fetchAsyncResult(taskID, additionTaskDetails, successCallback);
   */

  const findActiveOperation = (operationKey) => {
    const operations = Object.values(
      MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.PT.operations
    );
    return operations.find((data) => data.key === operationKey);
  };
  const handleSuccess = (additionTaskDetails, successCallback, dataList) => {
    return async (res) => {
      if (res.data.task_status === 'SUCCESS') {
        const activeOperationData = findActiveOperation(
          additionTaskDetails.operation
        );
        setActiveOperation(activeOperationData);

        if (res.data?.parameters?.dataset_id) {
          const activeDatasetId = dataList.find(
            (dataset) => dataset.id === Number(res.data.parameters.dataset_id)
          );
          setActiveDataset(activeDatasetId);
        }

        updateTaskDetails(res.data);
        successCallback();
      } else {
        updateTaskDetails({});
      }
    };
  };

  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) => {
    return axios
      .get(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}`)
      .then(async (res) => {
        const dataList = await fetchDatasetList(
          res?.data?.parameters?.project_id
        );
        handleSuccess(
          additionTaskDetails,
          successCallback,
          dataList?.data
        )(res);
      })
      .catch(() => {});
  };

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });
  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project?.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );
  /**
   * @function terminateTask
   * @description API call for terminating task in task manager
   * @param {string} taskID - task id
   
   * @returns {Promise} AxiosPromise
   * @example terminateTask(taskID);
   */
  const terminateTask = (taskID) =>
    axios
      .delete(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function fetchExisistingRules
   * @description API call to fetch ExisistingRules data from server
   * @param {object} body -data for ExisistingRules operation result
  
   * @returns {Promise} AxiosPromise
   * @example fetchExisistingRules(body);
   */
  const fetchExisistingRules = (body) =>
    axios
      .get(
        `${
          MT_BASE_URL +
          ApiEndpoints.MT.MT_PT_FETCH_EXISISTING_RULES +
          body.project_id
        }`
      )
      .then(
        (res) => {
          updateTestingConfig(res.data);
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function onMetricSelectionSubmit
   * @description API call will send payload(body) to perform metric
   *  selection operation on server which returns a task id
   * @param {object} body -data for MetricSelection operation result
  
   * @returns {Promise} AxiosPromise
   * @example onMetricSelectionSubmit(body);
   */
  const onMetricSelectionSubmit = (body) =>
    axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.MT_PT_METRIC_SELECTION}`, body)
      .then(
        (res) => {
          return res.data;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function onMonitoringRulesSubmit
   * @description API call will send payload(body) to perform monitoring
   *  rules operation on server which returns a task id
   * @param {object} body -data for MonitoringRules operation result
   *  @param {function} errorCallback - Callback function to execute
   *  when any error happens during the calling of API
   * @returns {Promise} AxiosPromise
   * @example onMonitoringRulesSubmit(body,errorCallback);
   */

  const [isBlinking, setIsBlinking] = useState(true);
  const onMonitoringRulesSubmit = (body, errorCallback) =>
    axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.MT_PT_SET_MONITORING_RULES}`, body)
      .then(
        (res) => {
          setIsBlinking(false);
          return res.data;
        },
        (error) => {
          errorCallback(error);
        }
      );

  /**
   * @function onOngoingComparisionSubmit
   * @description API call will send payload(body) to
   *  perform ongoing comparison operation on server which returns a task id
   * @param {object} body -data for OngoingComparision operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
   
   * @returns {Promise} AxiosPromise
   * @example onOngoingComparisionSubmit(body, successCallback);
   */
  const onOngoingComparisionSubmit = (body, successCallback) =>
    axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.MT_PT_ONGOING_COMPARISION}`, body)
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );

  /**
   * @function onAucListCurvesSubmit
   * @description API call to get auc-list curves operation result
   * @param {object} body -data for auc-list curves operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example onAucListCurvesSubmit(body, successCallback);
   */
  // For AUC list curves request
  const onAucListCurvesSubmit = (body, successCallback) =>
    axios
      .post(
        `${MT_BASE_URL + ApiEndpoints.MT.MT_CS_AUC_LIFT_CURVES_SUBMIT}`,
        body
      )
      .then(
        () => {
          successCallback();
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function onClassificationPlotSubmit
   * @description API call to get classification plot submit operation result
   * @param {object} body -data for classification plot submit operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example onClassificationPlotSubmit(body, successCallback);
   */
  // For Classification Plot request
  const onClassificationPlotSubmit = (body, successCallback) =>
    axios
      .post(
        `${MT_BASE_URL + ApiEndpoints.MT.MT_CS_CLASSIFICATION_PLOTS_SUBMIT}`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );

  /**
   * @function fetchAllModelDetails
   * @description API call to get all models details associated with the active project.
   * @param {number} projectId - projectId of the active project.
   * @param {string} field - fields to be fetched
   * @returns {Promise} AxiosPromise
   * @example fetchAllModelDetails(projectId);
   */

  const fetchAllModelDetails = (projectId, field) => {
    if (projectId) {
      axios
        .get(`${ME_BASE_URL}${ApiEndpoints.MT.MT_SS_ALL_MODELS_DETAILS}`, {
          params: {
            project_id: projectId,
            ...(field ? { fields: field } : {}),
          },
        })
        .then(
          (res) => {
            setAllModelData(res.data);
            return res.data;
          },
          () => {}
        );
    }
    // no return statement
  };

  /**
   * @function fetchTargetVariableList
   * @description API call to get all the target variables/columns in the dataset.
   * @param {string} datasetName - name of the dataset.
   * @returns {Promise} AxiosPromise
   * @example fetchTargetVariableList(datasetName);
   */
  const fetchTargetVariableList = (datasetName) =>
    axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.GET_TARGET_LIST}`, datasetName)
      .then(
        (res) => {
          setTargetVariables(res.data.valid_targets);
        },
        () => {}
      );

  /**
   * @function markTaskReadUnreadPerformanceTesting
   * @description API call for mark task read
   *  unread of performance testing sub module job queue tasks
   * @param {object} taskParams -task parameters of mark task read unread of sampling sub module
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example markTaskReadUnreadPerformanceTesting(taskParams,successCallback);
   */
  const markTaskReadUnreadPerformanceTesting = (taskParams, successCallback) =>
    axios
      .get(`${MT_BASE_URL + ApiEndpoints.MT.MT_JOB_QUEUE_MARK_READ_UNREAD}`, {
        params: taskParams,
      })
      .then(
        () => {
          successCallback();
        },
        () => {}
      );

  const mtRerunOperation = (body) => {
    return axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.MT_RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function addTableAndChartForMonitoring
   * @description API call for fetching table and chart column details
   * @param {object} payload - object containing project_id and data
   * @returns {Promise} AxiosPromise
   * @example addTableAndChartForMonitoring(datasetName);
   */
  const onAddTableChartClick = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.MT + ApiEndpoints.MT.ADD_TABLE_AND_CHART
        }`,
        payload
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );

  return {
    isBlinking,
    setIsBlinking,
    activeOperation,
    setActiveOperation,
    mtRerunOperation,

    activeDataset,
    setActiveDataset,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchPTModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    resetTaskDetails,
    taskDetails,

    fetchAsyncResult,

    fetchAllModelDetails,
    allModelData,

    datasetList,
    fetchDatasetList,

    onMonitoringRulesSubmit,
    onMetricSelectionSubmit,
    onOngoingComparisionSubmit,
    onAucListCurvesSubmit,
    onClassificationPlotSubmit,

    fetchTargetVariableList,
    targetVariables,

    testingConfig,
    fetchExisistingRules,

    terminateTask,

    updateDatasetCells,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    markTaskReadUnreadPerformanceTesting,

    datasetColumnDetails,
    fetchDatasetColumnDetails,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
    onAddTableChartClick,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchPTModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    project,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchConsoleFileList,
    fetchAsyncResultSplit,
  };
}
